import { useRecoilState } from 'recoil';

import { reporterIsBypassAndSignPrelim, ReportStatus, ReportStatuses } from '../state';
import { useBypassPrelimEnabled } from '../hooks/useBypassPrelimEnabled';
import { unreachableCaseError } from 'types';

import {
  ActionButton,
  ButtonLabels,
  GetButtonContentParams,
} from './ActionButton/Fields.ActionButton';
import { ButtonContentForIdleStatus } from './ActionButton/ButtonContentForIdleStatus';
import { FieldsButtonProps, SubmitType, VariantToStatusMapType } from './types';

const PRELIM_OPTIONS = ['Prelim', 'Bypass and Prelim'];

const VARIANT_PRIMARY: ReportStatuses[] = [
  ReportStatus.Idle,
  ReportStatus.AddendumAdd,
  ReportStatus.AddendumCancelling,
  ReportStatus.AddendumSubmitting,
  ReportStatus.ProvisionalAddendumSubmit,
  ReportStatus.Cancelling,
  ReportStatus.ProvisionalSubmit,
  ReportStatus.Submitted,
  ReportStatus.Submitting,
  ReportStatus.CancellingPrelim,
  ReportStatus.SubmittingPrelim,
];
const VARIANT_YELLOW: ReportStatuses[] = [ReportStatus.ProvisionalPrelimSubmit];
const VARIANT_SECONDARY: ReportStatuses[] = [ReportStatus.Init];

export const variantByStatus: VariantToStatusMapType = {
  primary: VARIANT_PRIMARY,
  yellow: VARIANT_YELLOW,
  secondary: VARIANT_SECONDARY,
};

const SUBMIT_PRELIM_BUTTON_LABELS: ButtonLabels = {
  buttonTooltip: 'Cannot sign prelim report until changes are saved.',
  buttonGroupAriaLabel: 'Button group for signing prelim',
  buttonDataTestId: 'sign-prelim-button',
  splitButtonDataTestId: 'submit-prelim-split-button',
};

export default function FieldsPrelimButton({
  countdownSeconds,
  status,
  onSubmit,
  onCancelSubmit,
  disabled,
  isClaimedByMe,
  isOtherCaseInGroupSubmitting,
  isCountdownMode,
}: FieldsButtonProps): React.ReactElement {
  const isBypassSignPrelimEnabled = useBypassPrelimEnabled();
  const [, setIsBypassAndSign] = useRecoilState(reporterIsBypassAndSignPrelim);
  const isProvisionalSubmit = status === ReportStatus.ProvisionalSubmit;

  const getButtonContent = ({
    isRecording,
    isBypass,
  }: GetButtonContentParams): React.ReactElement | string => {
    switch (status) {
      case ReportStatus.Idle:
      case ReportStatus.Init:
      case ReportStatus.AddendumAdd:
      case ReportStatus.AddendumCancelling:
      case ReportStatus.AddendumSubmitting:
      case ReportStatus.Cancelling:
      case ReportStatus.Submitted:
      case ReportStatus.Submitting:
      case ReportStatus.ProvisionalAddendumSubmit:
      case ReportStatus.ProvisionalSubmit:
        return (
          <ButtonContentForIdleStatus
            isRecording={isRecording}
            isOtherCaseInGroupSubmitting={isOtherCaseInGroupSubmitting}
            isBypass={isBypass}
            content={{
              signLabel: 'Prelim',
              otherCaseInGroupSubmittingLabel: 'Signing Prelim With Group',
            }}
          />
        );
      case ReportStatus.SubmittingPrelim:
        return 'Signing Prelim...';
      case ReportStatus.CancellingPrelim:
      case ReportStatus.ProvisionalPrelimSubmit:
        // Submit is the default state so this makes it show consistent regardless of the underlying state
        return isClaimedByMe ? 'Cancel Prelim Signing' : 'Prelim';
      default:
        return unreachableCaseError(status);
    }
  };

  return (
    <ActionButton
      countdownSeconds={!isProvisionalSubmit ? countdownSeconds : null}
      status={status}
      submitType={SubmitType.Prelim}
      onSubmit={onSubmit}
      onCancelSubmit={onCancelSubmit}
      getContent={getButtonContent}
      variantByStatus={variantByStatus}
      disabled={disabled || isOtherCaseInGroupSubmitting}
      options={PRELIM_OPTIONS}
      labels={SUBMIT_PRELIM_BUTTON_LABELS}
      isBypassEnabled={isBypassSignPrelimEnabled}
      setBypass={setIsBypassAndSign}
      isCountdownMode={!isProvisionalSubmit && isCountdownMode}
    />
  );
}
